import { RouteManagementRow, RouteType } from "../../types/route.type";
import { numberWithCommas } from "../../utils";
import {
  CATALOGUE_CELL_PADDING,
  STRIPE_BACKGROUND,
  catalogueRowHeight,
} from "../Product/shared/constants";
import Skeleton from "../ReprintAuth/Skeleton";
import {
  LIVE_MODE_LABEL,
  QR_PAYMENT_LABEL,
  ROUTE_TYPE_LABEL,
  TEST_MODE_LABEL,
  buildRouteInUseMessage,
  canSwitchQrPayment,
  isRouteInUse,
  readQrSwitchTooltip,
} from "./routeText";
import Box from "@mui/material/Box";
import Chip, { ChipProps } from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Theme, alpha } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";

const BASE_COLUMNS = [
  "รหัสสาย",
  "ชื่อสาย",
  "เลขสาย",
  "ประเภท",
  "สถานะ",
  "ร้านค้า",
  "พนักงานขาย",
];
const ACTION_COLUMN = "การดำเนินการ";

/** A small chip and a small icon button are the tallest things in a row. */
const ROW_CONTENT_HEIGHT = 30;
const ROW_HEIGHT = catalogueRowHeight(ROW_CONTENT_HEIGHT);

/** A branch has at most 16 ordinary routes and route 99, so the placeholder shows a full page. */
const SKELETON_ROWS = 8;

const ROUTE_TYPE_CHIP_COLOR: Record<RouteType, ChipProps["color"]> = {
  MAIN: "default",
  SPECIAL: "secondary",
};

// Tint behind a route still in test mode, so the routes not yet live stand out when scanning.
const TEST_MODE_ROW_TINT = 0.08;

const headCellSx = {
  borderRight: (theme: Theme) => `1px solid ${theme.palette.draft.main}`,
  "&:last-of-type": { borderRight: "none" },
};

const rowSx = (route: RouteManagementRow, index: number) => ({
  bgcolor: (theme: Theme) => {
    if (route.is_test_mode)
      return alpha(theme.palette.warning.main, TEST_MODE_ROW_TINT);
    return index % 2 === 1 ? STRIPE_BACKGROUND : "#fff";
  },
  height: ROW_HEIGHT,
  "& td, & th": { padding: `${CATALOGUE_CELL_PADDING}px` },
});

interface RouteTableProps {
  routes: RouteManagementRow[];
  loading: boolean;
  canWrite: boolean;
  canEditTestMode: boolean;
  canEditQr: boolean;
  onRename: (route: RouteManagementRow) => void;
  onToggleTestMode: (route: RouteManagementRow) => void;
  onToggleQr: (route: RouteManagementRow) => void;
  onDelete: (route: RouteManagementRow) => void;
}

export default function RouteTable({
  routes,
  loading,
  canWrite,
  canEditTestMode,
  canEditQr,
  onRename,
  onToggleTestMode,
  onToggleQr,
  onDelete,
}: RouteTableProps) {
  const columns = canWrite ? [...BASE_COLUMNS, ACTION_COLUMN] : BASE_COLUMNS;

  const handleClickRename = (route: RouteManagementRow) => () => {
    onRename(route);
  };

  const handleClickTestMode = (route: RouteManagementRow) => () => {
    onToggleTestMode(route);
  };

  const handleClickQr = (route: RouteManagementRow) => () => {
    onToggleQr(route);
  };

  const handleClickDelete = (route: RouteManagementRow) => () => {
    onDelete(route);
  };

  const renderActions = (route: RouteManagementRow) => {
    if (route.route_type === "SPECIAL") {
      return (
        <Typography variant="caption" color="text.secondary">
          อ่านอย่างเดียว
        </Typography>
      );
    }

    const isInUse = isRouteInUse(route);
    const deleteTooltip = isInUse
      ? buildRouteInUseMessage(route.route_code, route)
      : "ลบสาย";
    const testModeTooltip = route.is_test_mode
      ? "ปิดโหมดทดสอบ (ใช้งานจริง)"
      : "กลับเป็นโหมดทดสอบ";
    const qrTooltip = readQrSwitchTooltip(route);
    const isQrSwitchDisabled = !canSwitchQrPayment(route);

    return (
      <Stack direction="row" gap={0.5}>
        <Tooltip title="แก้ชื่อสาย">
          <IconButton
            size="small"
            color="primary"
            onClick={handleClickRename(route)}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canEditTestMode && (
          <Tooltip title={testModeTooltip}>
            <IconButton
              size="small"
              color="warning"
              onClick={handleClickTestMode(route)}
            >
              <ScienceOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canEditQr && (
          <Tooltip title={qrTooltip}>
            <Box component="span">
              <IconButton
                size="small"
                color="success"
                disabled={isQrSwitchDisabled}
                onClick={handleClickQr(route)}
              >
                <QrCode2OutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        )}
        <Tooltip title={deleteTooltip}>
          {/* A disabled button fires no events, so the span carries the tooltip. */}
          <Box component="span">
            <IconButton
              size="small"
              color="error"
              disabled={isInUse}
              onClick={handleClickDelete(route)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column} sx={headCellSx}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <Skeleton
                columns={columns.length}
                rows={SKELETON_ROWS}
                rowHeight={ROW_HEIGHT}
              />
            )}
            {!loading && routes.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    สาขานี้ยังไม่มีสายส่ง
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              routes.map((route, index) => (
                <TableRow key={route.uuid} sx={rowSx(route, index)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {route.route_code}
                    </Typography>
                  </TableCell>
                  <TableCell>{route.route_name}</TableCell>
                  <TableCell>{route.route_number ?? "-"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={ROUTE_TYPE_CHIP_COLOR[route.route_type]}
                      label={ROUTE_TYPE_LABEL[route.route_type]}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" gap={0.5}>
                      {route.is_test_mode ? (
                        <Chip size="small" color="warning" label={TEST_MODE_LABEL} />
                      ) : (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={LIVE_MODE_LABEL}
                        />
                      )}
                      {route.qr_payment_allowed && (
                        <Chip size="small" color="success" label={QR_PAYMENT_LABEL} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {numberWithCommas(route.live_store_count)}
                  </TableCell>
                  <TableCell align="right">
                    {numberWithCommas(route.active_staff_count)}
                  </TableCell>
                  {canWrite && <TableCell>{renderActions(route)}</TableCell>}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
